/**
 * These decorators all apply the information they collect (whether class, method, or parameter data) as tagged metadata on the class's constructor
 */
import {INJECT_METADATA_KEY, INJECTABLE_METADATA_KEY, OPTIONAL_METADATA_KEY, POSTCONSTRUCT_ASYNC_METADATA_KEY, POSTCONSTRUCT_SYNC_METADATA_KEY, REFLECT_PARAMS, REFLECT_RETURN, RELEASE_METADATA_KEY} from './constants';
import {InjectableId} from './injector';

// Help user locate misapplied decorators.
function targetHint(target: Function): string {
	let hint: string | undefined;
	if (target) {
		hint = target.name;
		if ((!hint) && target.constructor) {
			hint = target.constructor.name;
		}
	}
	return hint ?? '';
}

// Validate that 'target' is a class constructor function.
function isClassConstructor(target: any) {
	if (typeof target === 'function' && target.hasOwnProperty('prototype')) {
		return target.prototype.constructor === target;
	}
	return false;
}

// Ensure consistency in our meta-data name getting/setting.
function makeParamIdxKey(idx: number): string {
	return `index-${idx}`;
}

// Key for @Inject/@Optional metadata stored on a method parameter.
function makeMethodParamIdxKey(methodName: string | symbol, idx: number): string {
	return `${String(methodName)}:index-${idx}`;
}

// Validate that the specified target is a parameter of a class constructor
function validateConstructorParam(decorator: string, target: Function, idx: number): string {
	if (!isClassConstructor(target)) {
		throw new Error('@' + decorator + ' is not valid here [' + targetHint(target) + ']');
	}
	return makeParamIdxKey(idx);
}

// Validate that the target is an instance method parameter (non-constructor).
function validateMethodParam(decorator: string, target: object, methodName: string | symbol, idx: number): string {
	if (typeof target !== 'object' || typeof (target as any).constructor !== 'function') {
		throw new Error('@' + decorator + ' is not valid here [' + targetHint(target as Function) + ']');
	}
	if (!Reflect.hasOwnMetadata(REFLECT_PARAMS, target, methodName)) {
		// REFLECT_PARAMS not yet set (decoration order), so we cannot pre-validate the method; accept silently.
	}
	return makeMethodParamIdxKey(methodName, idx);
}

// Validate the decorator was only applied once.
function validateSingleConstructorParam(decorator: string, target: Function, idx: number): string {
	const propKey = validateConstructorParam(decorator, target, idx);
	if (Reflect.hasOwnMetadata(decorator, target, propKey)) {
		throw new Error('@' + decorator + ' applied multiple times [' + target.constructor.name + ']');
	}
	return propKey;
}

/**
 * Placed just before the class declaration, this class decorator applies metadata to the class constructor indicating that the user intends to bind the class into the container.
 * This decorator will throw if not placed on a class declaration, or if placed more than once on a class declaration.
 */
export function Injectable(): ClassDecorator {
	/**
	 * @param target   The constructor function of the class that is being decorated
	 * @returns Undefined (nothing), as this decorator does not modify the constructor in any way.
	 */
	return function (target: Function): void {
		if (Reflect.hasOwnMetadata(INJECTABLE_METADATA_KEY, target)) {
			throw new Error('@Injectable applied multiple times [' + targetHint(target) + ']');
		}
		Reflect.defineMetadata(INJECTABLE_METADATA_KEY, true, target);
	};
}

/**
 * Placed just before a constructor parameter, this parameter decorator allows for specificity and control over the type of Object that will be injected into the parameter.
 * In the absence of this decorator the container will use whatever is bound to a parameter's type (or throw an error if it is unable to recognize the type).
 * This decorator may also be placed on a parameter of a method annotated with @PostConstruct, in which case the container will resolve and inject the value before invoking that method.
 *
 * @param id  The identifier of the bound type that should be injected.
 */
export function Inject(id: InjectableId<any>): ParameterDecorator {
	/**
	 * @param target  The constructor function of the class (for constructor params), or the class prototype (for method params).
	 * @param parameterName The name of the parameter (undefined for constructor params, the method name for method params)
	 * @param parameterIndex The ordinal index of the parameter in the function’s parameter list
	 * @returns Undefined (nothing), as this decorator does not modify the parameter in any way.
	 */
	return function (target: object, parameterName: string | symbol | undefined, parameterIndex: number): void {
		if (id === undefined) {
			throw new Error('Undefined id passed to @Inject [' + targetHint(target as Function) + ']');
		}
		if (parameterName === undefined) {
			// Constructor parameter
			const paramKey = validateSingleConstructorParam('Inject', target as Function, parameterIndex);
			Reflect.defineMetadata(INJECT_METADATA_KEY, id, target, paramKey);
		}
		else {
			// Method parameter — silently accepted (intended for @PostConstruct methods)
			const paramKey = validateMethodParam('Inject', target, parameterName, parameterIndex);
			Reflect.defineMetadata(INJECT_METADATA_KEY, id, target, paramKey);
		}
	};
}

/**
 * This is a helper function used by the container to retrieve the @Inject metadata for a specifically indexed constructor parameter
 *
 * @param target  The constructor function of the class (we don't allow @Inject on anything else).
 * @param parameterIndex    The ordinal index of the parameter in the constructor’s parameter list
 * @see Inject
 */
export function _getInjectedIdAt(target: Function, parameterIndex: number): InjectableId<any> {
	return Reflect.getMetadata(INJECT_METADATA_KEY, target, makeParamIdxKey(parameterIndex)) as InjectableId<any>;
}

/**
 * Retrieve the @Inject metadata for a specifically indexed parameter of a named method.
 */
export function _getInjectedIdForMethod(prototype: object, methodName: string | symbol, parameterIndex: number): InjectableId<any> {
	return Reflect.getMetadata(INJECT_METADATA_KEY, prototype, makeMethodParamIdxKey(methodName, parameterIndex)) as InjectableId<any>;
}

/**
 * Placed just before a constructor parameter, this parameter decorator signals the container that it should supply the 'alt' constant value (undefined by default) if for *any* reason it is unable to otherwise resolve the type of the parameter.
 * This decorator may also be placed on a parameter of a method annotated with @PostConstruct.
 * WARNING!  It is your responsibility to ensure that alt is of the appropriate type/value.
 */
export function Optional(alt?: any): ParameterDecorator {
	/**
	 * @param target  The constructor function of the class (for constructor params), or the class prototype (for method params).
	 * @param parameterName The name of the parameter (undefined for constructor params, the method name for method params)
	 * @param parameterIndex The ordinal index of the parameter in the function’s parameter list
	 * @returns Undefined (nothing), as this decorator does not modify the parameter in any way.
	 */
	return function (target: object, parameterName: string | symbol | undefined, parameterIndex: number): void {
		if (parameterName === undefined) {
			// Constructor parameter
			const paramKey = validateSingleConstructorParam('Optional', target as Function, parameterIndex);
			Reflect.defineMetadata(OPTIONAL_METADATA_KEY, {value: alt}, target, paramKey);
		}
		else {
			// Method parameter — silently accepted (intended for @PostConstruct methods)
			const paramKey = validateMethodParam('Optional', target, parameterName, parameterIndex);
			Reflect.defineMetadata(OPTIONAL_METADATA_KEY, {value: alt}, target, paramKey);
		}
	};
}

/**
 * This is a helper function used by the container to retrieve the @Optional metadata for a specifically indexed constructor parameter
 *
 * @param target  The constructor function of the class (we don't allow @Optional on anything else).
 * @param parameterIndex    The ordinal index of the parameter in the constructor’s parameter list
 * @see Optional
 * @returns an object containing the value provided in the decorator, or undefined if no annotation was present.
 */
export function _getOptionalDefaultAt(target: Function, parameterIndex: number): { value: any } {
	return Reflect.getMetadata(OPTIONAL_METADATA_KEY, target, makeParamIdxKey(parameterIndex)) as { value: any }; // See the @Optional decorator before making any changes here.
}

/**
 * Retrieve the @Optional metadata for a specifically indexed parameter of a named method.
 */
export function _getOptionalDefaultForMethod(prototype: object, methodName: string | symbol, parameterIndex: number): { value: any } {
	return Reflect.getMetadata(OPTIONAL_METADATA_KEY, prototype, makeMethodParamIdxKey(methodName, parameterIndex)) as { value: any };
}

/**
 * Placed just before a class method, this method decorator flags a method that should be called after an object has been instantiated by the container, but before it is put into service.
 * The method will be assumed to be synchronous unless the method signature explicitly declares its return type to be ": Promise<something>".
 * Parameters will be resolved by the container just as they are for constructors.
 * This decorator will throw if placed on a non-method or a static method of a class, or if placed on a method more than once, or if placed on more than one method for a class.
 */
export function PostConstruct(): MethodDecorator {
	/**
	 * @param prototypeOrConstructor   The prototype of the class (we don't allow @PostConstruct on anything other than a class instance method.
	 * @param methodName   The name of the method.
	 * @param _descriptor   The Property Descriptor for the method.
	 * @returns Undefined (nothing), as this decorator does not modify the method in any way.
	 */
	return function (target: Object, methodName: string | symbol, _descriptor: PropertyDescriptor) {
		if (typeof target !== 'object' || typeof target.constructor !== 'function') {
			throw new Error('@PostConstruct not applied to instance method [' + target.toString() + '/' + methodName.toString() + ']');
		}
		if (Reflect.hasOwnMetadata(POSTCONSTRUCT_SYNC_METADATA_KEY, target.constructor) || Reflect.hasOwnMetadata(POSTCONSTRUCT_ASYNC_METADATA_KEY, target.constructor)) {
			throw new Error('@PostConstruct applied multiple times [' + targetHint(target.constructor) + ']');
		}
		const rt = Reflect.getMetadata(REFLECT_RETURN, target, methodName);
		if (typeof rt === 'function') {
			Reflect.defineMetadata(POSTCONSTRUCT_ASYNC_METADATA_KEY, methodName, target.constructor);
		}
		else {
			Reflect.defineMetadata(POSTCONSTRUCT_SYNC_METADATA_KEY, methodName, target.constructor);
		}
	};
}

/**
 * Placed just before a class method, this decorator identifies a method which should be called when an object is removed from service.
 * If invoked by the container, the container will drop any references it has to the object when the method returns.
 * Note that this decorator is *not* a guarantee (or even an implication) that the decorated method will be called (JavaScript has no mechanism to enforce such a contract).
 * This decorator simply serves as a flag to indicate a method which is intended to clean up resources allocated by the object *which would not otherwise be garbage collected*.
 * You should *not* use this decorator as a general "object finalization" method.  It has very limited scope and purpose.
 * The decorated method must complete normally (no throwing), as "release" is not an abort-able process.
 * This decorator will throw if placed on a non-method or a static method of a class, or if placed on a method more than once, or if placed on more than one method for a class.
 * The @see InvokeReleaseMethod helper function can search for and invoke the @Release decorated method of an object.
 * Also @see Container.releaseSingletons for the intended usage of this decorator.
 * It is intended that after the @Release decorated method of an object is called, that object will not be used again, but this is of course not enforced).
 */
export function Release(): MethodDecorator {
	/**
	 * @param prototypeOrConstructor   The prototype of the class (we don't allow @Release on anything other than a class instance method.
	 * @param methodName   The name of the method.
	 * @param _descriptor   The Property Descriptor for the method.
	 * @returns Undefined (nothing), as this decorator does not modify the method in any way.
	 */
	return function (target: Object, methodName: string | symbol, _descriptor: PropertyDescriptor) {
		if (typeof target !== 'object' || typeof target.constructor !== 'function') {
			throw new Error('@Release not applied to instance method [' + target.toString() + '/' + methodName.toString() + ']');
		}
		if (Reflect.hasOwnMetadata(RELEASE_METADATA_KEY, target.constructor)) {
			throw new Error('@Release applied multiple times [' + targetHint(target.constructor) + ']');
		}
		Reflect.defineMetadata(RELEASE_METADATA_KEY, methodName, target.constructor);
	};
}
